import { encode, decode } from '@msgpack/msgpack'
import type { Client, WSOptions, Logger } from './types'

/**
 * 判断字节流是否为 GZIP 格式
 */
function isGzip(u8: Uint8Array) {
  return u8 && u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b
}

/**
 * 解压 GZIP 字节流
 */
async function gunzip(u8: Uint8Array): Promise<Uint8Array> {
  const ds = new (globalThis as any).DecompressionStream('gzip')
  const writer = ds.writable.getWriter()
  writer.write(u8)
  writer.close()
  const reader = ds.readable.getReader()
  const chunks: Uint8Array[] = []
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
  }
  const total = chunks.reduce((n, c) => n + c.length, 0)
  const out = new Uint8Array(total)
  let off = 0
  for (const c of chunks) { out.set(c, off); off += c.length }
  return out
}

/**
 * WebSocket 客户端实现
 * 封装连接管理、消息收发、心跳与自动重连
 */
export class WsClient implements Client {
  private options: WSOptions
  private ws: WebSocket | null = null
  private logger: Logger
  private listeners: { [k: string]: Set<Function> } = { connected: new Set(), disconnected: new Set(), error: new Set(), message: new Set() }
  private hbTimer: any = null
  private lastActivity = 0
  private retries = 0

  constructor(options: WSOptions, logger: Logger) {
    this.options = options
    this.logger = logger
  }

  /**
   * 建立 WebSocket 连接
   */
  async connect(): Promise<void> {
    if (this.ws) this.ws.close()
    this.ws = this.options.protocols && this.options.protocols.length > 0
      ? new WebSocket(this.options.url, this.options.protocols)
      : new WebSocket(this.options.url)
    this.ws.binaryType = 'arraybuffer'
    await new Promise<void>((resolve, reject) => {
      const ws = this.ws as WebSocket
      let opened = false
      ws.onopen = () => {
        opened = true
        this.lastActivity = Date.now()
        this.emit('connected')
        this.startHeartbeat()
        this.retries = 0
        resolve()
      }
      ws.onclose = (ev) => {
        this.stopHeartbeat()
        this.emit('disconnected')
        if (!opened) {
          reject(new Error(`WebSocket closed before open: code=${(ev as any).code || ''} reason=${(ev as any).reason || ''}`))
          return
        }
        this.tryReconnect()
      }
      ws.onerror = (e) => {
        this.emit('error', e)
        if (!opened) reject(e as any)
      }
      ws.onmessage = async (ev) => {
        this.lastActivity = Date.now()
        try {
          const data: any = ev.data
          let msg: any
          if (typeof data === 'string') {
            msg = JSON.parse(data)
          } else {
            let buf: ArrayBuffer
            if (data instanceof ArrayBuffer) {
              buf = data
            } else if (typeof Blob !== 'undefined' && data instanceof Blob) {
              buf = await data.arrayBuffer()
            } else {
              throw new Error('Unsupported binary message type')
            }
            let u8: Uint8Array = new Uint8Array(buf)
            if (isGzip(u8)) {
              try { u8 = await gunzip(u8 as any) as any } catch {}
            }
            msg = decode(u8 as any)
          }
          this.emit('message', msg)
        } catch (err) {
          this.emit('error', err)
        }
      }
    })
  }

  /**
   * 主动断开连接
   */
  disconnect(): void {
    this.stopHeartbeat()
    if (this.ws) this.ws.close()
    this.ws = null
  }

  /**
   * 当前是否处于已连接状态
   */
  isConnected(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN
  }

  /**
   * 发送消息（支持 JSON 与 MessagePack）
   */
  send(type: string, payload?: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    const codec = this.options.codec || 'msgpack'
    if (codec === 'json') {
      const outer = JSON.stringify({ type, payload })
      this.ws.send(outer)
    } else {
      const encodedPayload = payload !== undefined ? encode(payload) : undefined
      const outer = encode({ type, payload: encodedPayload })
      this.ws.send(outer)
    }
  }

  /**
   * 订阅事件
   */
  on(event: 'connected' | 'disconnected' | 'error' | 'message', handler: Function): void {
    this.listeners[event].add(handler)
  }

  /**
   * 取消订阅事件
   */
  off(event: 'connected' | 'disconnected' | 'error' | 'message', handler: Function): void {
    this.listeners[event].delete(handler)
  }

  /**
   * 分发事件到监听器
   */
  private emit(event: 'connected' | 'disconnected' | 'error' | 'message', payload?: any) {
    for (const h of this.listeners[event]) {
      try { (h as any)(payload) } catch (e) { this.logger.error(e) }
    }
  }

  /**
   * 启动心跳：定期发送 ping 并检测超时
   */
  private startHeartbeat() {
    const hb = this.options.heartbeat || { interval: 15000, timeout: 30000 }
    this.stopHeartbeat()
    this.hbTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
      const now = Date.now()
      if (now - this.lastActivity > hb.timeout) {
        this.logger.warn('heartbeat timeout')
        this.ws.close()
        return
      }
      try {
        const payload = hb.payload !== undefined ? hb.payload : undefined
        this.send('ping', payload)
      } catch {}
    }, hb.interval)
  }

  /**
   * 停止心跳
   */
  private stopHeartbeat() {
    if (this.hbTimer) clearInterval(this.hbTimer)
    this.hbTimer = null
  }

  /**
   * 自动重连：按配置的重试次数与间隔尝试重连
   */
  private tryReconnect() {
    const opt = this.options.retry || { retries: 3, interval: 3000 }
    if (this.retries >= opt.retries) return
    this.retries += 1
    setTimeout(() => { this.connect().catch(() => {}) }, opt.interval)
  }
}
